import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLang } from '../contexts/LanguageContext';
import { STORE } from '../lib/i18n';
import { waLink } from '../lib/whatsapp';
import { useLandingSEO } from '../lib/seo';
import CategoryPrice from '../components/CategoryPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  'refrigerators':  '/img/cat-refrigerators.jpg',
  'stoves':         '/img/cat-stoves.jpg',
  'washers-dryers': '/img/cat-washers-dryers.jpg',
  'microwaves':     '/img/cat-microwaves.jpg',
};

// Hard-coded categories so the catalog renders even when the backend is empty.
// Kept in sync with the admin "categories" list but no longer depends on it.
const CATEGORIES = [
  { slug: 'refrigerators',  name_en: 'Refrigerators',       name_es: 'Neveras' },
  { slug: 'stoves',         name_en: 'Stoves & Ranges',     name_es: 'Estufas' },
  { slug: 'washers-dryers', name_en: 'Washers & Dryers',    name_es: 'Lavadoras y Secadoras' },
  { slug: 'microwaves',     name_en: 'Microwaves',          name_es: 'Microondas' },
];

const useReveal = (deps = []) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal:not(.in)');
    if (items.length === 0) return undefined;
    const inViewportNow = (node) => {
      const r = node.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh && r.bottom > 0;
    };
    items.forEach(i => { if (inViewportNow(i)) i.classList.add('in'); });

    let timer = null;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
      }, { threshold: 0.12 });
      items.forEach(i => io.observe(i));
      timer = setTimeout(() => { items.forEach(i => i.classList.add('in')); }, 700);
      return () => { io.disconnect(); if (timer) clearTimeout(timer); };
    }
    items.forEach(i => i.classList.add('in'));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
};

export default function Landing() {
  const { lang, t } = useLang();
  const containerRef = useReveal([]);

  useLandingSEO();

  // Hours
  const today = new Date().getDay();
  const todayHours = STORE.hours.find(h => h.day === today);
  const todayLabel = todayHours ? `${t(`days.${today}`)} · ${todayHours.openLabel}` : '';

  // Hash scroll
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const el = document.getElementById(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, []);

  // Ping backend once so analytics still fires; swallow errors.
  useEffect(() => { axios.get(`${API}/health`).catch(() => {}); }, []);

  const shippingWa = lang === 'es'
    ? 'Hola Nathanael Appliances Corp, quisiera preguntar sobre envíos a República Dominicana.'
    : 'Hi Nathanael Appliances Corp, I\'d like to ask about shipping to the Dominican Republic.';

  return (
    <div ref={containerRef}>
      {/* HERO */}
      <header className="hero" id="home">
        <div className="wrap">
          <div className="hero-grid">
            <div className="reveal">
              <span className="eyebrow">
                <span className="dot"></span>
                <span>{t('hero.eyebrow')}</span>
              </span>
              <h1>
                {t('hero.title.a')}<em>{t('hero.title.em')}</em>{t('hero.title.b')}
              </h1>
              <p className="hero-sub">{t('hero.sub')}</p>

              <div className="financing-pill" data-testid="financing-pill">
                <i className="fa-solid fa-tags"></i>
                <span>{t('hero.financing')}</span>
              </div>

              <div className="hero-cta">
                <Link to="/shop" className="btn btn-primary btn-lg" data-testid="hero-shop-btn">
                  <span>{t('hero.cta1')}</span>
                  <i className="fa-solid fa-arrow-right"></i>
                </Link>
                <button
                  type="button"
                  className="btn btn-affirm btn-lg"
                  data-testid="hero-financing-how-btn"
                  onClick={() => {
                    const el = document.getElementById('how-it-works');
                    if (!el) return;
                    const nav = document.querySelector('header, nav');
                    const offset = nav ? nav.getBoundingClientRect().height : 0;
                    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
                  }}
                >
                  <span className="affirm-logo-text" aria-hidden="true">affirm</span>
                  <span>{t('hero.financingHow')}</span>
                </button>
                <a href="#contact" className="btn btn-ghost btn-lg" data-testid="hero-visit-btn">
                  <i className="fa-solid fa-store"></i>
                  <span>{t('hero.cta2')}</span>
                </a>
              </div>
            </div>

            <div className="hero-media reveal" data-testid="hero-image">
              <img
                src="/img/storefront.jpg"
                alt="Nathanael Appliances Corp — storefront on Ogden Ave, Bronx"
              />
            </div>
          </div>

          <div className="infobar reveal" data-testid="info-bar">
            <div className="cell">
              <i className="fa-solid fa-location-dot"></i>
              <div>
                <div className="label">{t('info.visit')}</div>
                <a className="val" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(STORE.address)}`} target="_blank" rel="noopener noreferrer">{STORE.address}</a>
              </div>
            </div>
            <div className="cell">
              <i className="fa-solid fa-phone"></i>
              <div>
                <div className="label">{t('info.call')}</div>
                <a className="val" href={`tel:${STORE.phoneRaw}`}>{STORE.phone}</a>
              </div>
            </div>
            <div className="cell">
              <i className="fa-solid fa-clock"></i>
              <div>
                <div className="label">{t('info.today')}</div>
                <div className="val">{todayLabel}</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="how">
        <div className="wrap">
          <div className="section-head center reveal">
            <span className="section-kicker">{t('how.kicker')}</span>
            <h2>{t('how.title.a')}<em>{t('how.title.em')}</em>{t('how.title.b')}</h2>
            <p className="section-sub">{t('how.sub')}</p>
          </div>

          <div className="how-grid">
            {[1, 2, 3].map(n => (
              <div key={n} className="how-card reveal" data-testid={`how-step-${n}`}>
                <div className="how-num">{n}</div>
                <h3>{t(`how.${n}t`)}</h3>
                <p>{t(`how.${n}d`)}</p>
              </div>
            ))}
          </div>

          <div className="how-cta reveal">
            <a
              href={waLink(lang === 'es'
                ? 'Hola Nathanael Appliances Corp, tengo una pregunta sobre Affirm y financiamiento.'
                : 'Hi Nathanael Appliances Corp, I have a question about Affirm and financing.')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-wa btn-lg"
              data-testid="how-whatsapp-btn"
            >
              <i className="fa-brands fa-whatsapp"></i>
              <span>{t('how.wa')}</span>
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(STORE.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-lg"
              data-testid="how-visit-btn"
            >
              <i className="fa-solid fa-store"></i>
              <span>{t('how.visit')}</span>
            </a>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="ticker" aria-hidden="true">
        <div className="ticker-track">
          {[0, 1].map(i => (
            <span key={i}>
              <span>Refrigerators</span><span className="pill-dot"></span>
              <span>Stoves</span><span className="pill-dot"></span>
              <span>Washers</span><span className="pill-dot"></span>
              <span>Dryers</span><span className="pill-dot"></span>
              <span>Samsung</span><span className="pill-dot"></span>
              <span>LG</span><span className="pill-dot"></span>
              <span>Whirlpool</span><span className="pill-dot"></span>
              <span>Bosch</span><span className="pill-dot"></span>
              <span>Financing Available</span><span className="pill-dot"></span>
              <span>Shipping to DR</span><span className="pill-dot"></span>
            </span>
          ))}
        </div>
      </div>

      {/* PRODUCT CATEGORIES */}
      <section className="products" id="products">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="section-kicker">{t('products.kicker')}</span>
            <h2>{t('products.title.a')}<em>{t('products.title.em')}</em>{t('products.title.b')}</h2>
            <p className="section-sub">{t('products.sub')}</p>
          </div>

          <div className="cat-grid">
            {CATEGORIES.map(c => {
              const catName = lang === 'es' ? c.name_es : c.name_en;
              const waText = lang === 'es'
                ? `Hola Nathanael Appliances Corp, me interesan los modelos y precios actuales de ${catName}.`
                : `Hi Nathanael Appliances Corp, I'd like to ask about current models and prices for ${catName}.`;
              return (
                <a
                  key={c.slug}
                  href={waLink(waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cat-card reveal"
                  data-testid={`cat-card-${c.slug}`}
                >
                  <div className="cat-img">
                    <img src={CATEGORY_IMAGES[c.slug]} alt={catName} loading="lazy" />
                  </div>
                  <div className="cat-body">
                    <h3>{catName}</h3>
                    <CategoryPrice slug={c.slug} />
                    {c.slug !== 'microwaves' && <p>{t('shop.askModels')}</p>}
                    <span className="cat-cta">
                      <i className="fa-brands fa-whatsapp"></i>
                      <span>{t('cat.waCta')}</span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </span>
                  </div>
                </a>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: 48 }} className="reveal">
            <Link to="/shop" className="btn btn-primary btn-lg" data-testid="view-all-btn">
              <span>{t('products.viewAll')}</span>
              <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>

          <div className="brands reveal" data-testid="brands-row">
            <span className="brand">SAMSUNG</span>
            <span className="brand">LG</span>
            <span className="brand">Whirlpool</span>
            <span className="brand">Bosch</span>
          </div>
        </div>
      </section>

      {/* SHIPPING */}
      <section className="ship" id="shipping">
        <div className="wrap">
          <div className="ship-grid">
            <div className="reveal">
              <span className="section-kicker">{t('ship.kicker')}</span>
              <h2>{t('ship.title.a')}<em>{t('ship.title.em')}</em>{t('ship.title.b')}</h2>
              <p className="section-sub">{t('ship.sub')}</p>

              <div className="ship-cta" data-testid="shipping-cta">
                <a
                  href={waLink(shippingWa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-wa btn-lg"
                  data-testid="shipping-whatsapp-btn"
                >
                  <i className="fa-brands fa-whatsapp"></i>
                  <span>{t('ship.cta')}</span>
                </a>
                <a href={`tel:${STORE.phoneRaw}`} className="btn btn-ghost-light btn-lg">
                  <i className="fa-solid fa-phone"></i>
                  <span>{STORE.phone}</span>
                </a>
              </div>
            </div>

            <div className="ship-visual reveal">
              <div className="ship-flag">
                <span className="flag-do"></span>
                <span>{t('ship.flag')}</span>
              </div>
              <img
                src="/img/storefront.jpg"
                alt="Nathanael Appliances Corp storefront"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why">
        <div className="wrap">
          <div className="section-head center reveal">
            <span className="section-kicker">{t('why.kicker')}</span>
            <h2>{t('why.title.a')}<em>{t('why.title.em')}</em>{t('why.title.b')}</h2>
            <p className="section-sub">{t('why.sub')}</p>
          </div>

          <div className="why-grid why-grid-3">
            {[
              { i: 'fa-language' },
              { i: 'fa-plane-departure' },
              { i: 'fa-store' },
            ].map((c, idx) => {
              const n = idx + 1;
              return (
                <div key={n} className="why-card reveal">
                  <div className="ico"><i className={`fa-solid ${c.i}`}></i></div>
                  <h3>{t(`why.${n}t`)}</h3>
                  <p>{t(`why.${n}d`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="contact" id="contact">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="section-kicker">{t('contact.kicker')}</span>
            <h2>{t('contact.title.a')}<em>{t('contact.title.em')}</em>{t('contact.title.b')}</h2>
            <p className="section-sub">{t('contact.sub')}</p>
          </div>

          <div className="contact-grid">
            <div className="contact-card reveal" data-testid="hours-card">
              <h3 style={{ fontSize: 22, color: 'var(--navy)', margin: '0 0 20px', letterSpacing: '-.01em' }}>
                {t('contact.hoursTitle')}
              </h3>
              <ul className="hours">
                {[1, 2, 3, 4, 5, 6, 0].map(d => {
                  const hr = STORE.hours.find(h => h.day === d);
                  return (
                    <li key={d} className={d === today ? 'today' : ''} data-today-label={t('contact.today')}>
                      <span className="day">{t(`days.${d}`)}</span>
                      <span className="time">{hr ? hr.openLabel : ''}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="contact-info">
                <div className="info-row">
                  <i className="fa-solid fa-location-dot"></i>
                  <div>
                    <div className="label">{t('contact.address')}</div>
                    <div className="val">{STORE.address}</div>
                  </div>
                </div>
                <div className="info-row">
                  <i className="fa-solid fa-phone"></i>
                  <div>
                    <div className="label">{t('contact.phone')}</div>
                    <a className="val" href={`tel:${STORE.phoneRaw}`}>{STORE.phone}</a>
                  </div>
                </div>
              </div>

              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(STORE.address)}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} data-testid="directions-btn">
                <i className="fa-solid fa-diamond-turn-right"></i>
                <span>{t('contact.directions')}</span>
              </a>
            </div>

            <div className="map-wrap reveal" data-testid="map-embed">
              <iframe
                title="Nathanael Appliances Corp — Google Map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(STORE.address)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
